import { usePathname } from 'next/navigation'
import styled from 'styled-components'
import { screenSizes } from '@shipt/design-system-themes'
import { NextLink } from '@/features/shared/elements/Link'
import { ChevronRightIcon } from '@shipt/design-system-icons'
import { MobileFilterButton } from '@/features/shop/components/ProductFilter/MobileFilterButton'
import {
  findCategoryFacet,
  findCurrentCategoryByParams,
} from '@/features/shop/services/Search/utils'
import {
  useTotalActiveFiltersLength,
  useFindActiveArrayFiltersForFacetName,
} from '@/features/shop/services/Search/hooks'
import { useValidSearchParams } from '@/features/shared/hooks/search'
import { SkeletonLoader } from '@/features/shared/components/SkeletonLoader'
import { stringify } from '@/features/shared/utils/queryString'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { type Facet } from '@/features/shop/services/Search/types'
import { DynamicSearchFilterModal } from '@/features/shop/components/Modal/SearchFilterModal/DynamicSearchFilterModal'
import { Body, Display } from '@shipt/design-system-typography'
import { SEARCH_FILTER_TYPE } from '@/features/shared/constants/global'
import { hideScrollBar } from '@/features/shared/styles/effects'
interface SearchResultsHeaderProps {
  facets: Facet[]
  total_hits: number
  isFetching: boolean
}
export const SearchResultsHeader = ({
  facets,
  total_hits,
  isFetching,
}: SearchResultsHeaderProps) => {
  const { openDialog } = useHandleDialog()
  const pathname = usePathname() ?? ''

  const queryParams = useValidSearchParams()
  const currentCategory = findCurrentCategoryByParams(facets, queryParams)
  const findActiveFiltersForBrand = useFindActiveArrayFiltersForFacetName()
  const activeBrandFilters = findActiveFiltersForBrand(facets, SEARCH_FILTER_TYPE.BRAND)
  const activeFiltersLength = useTotalActiveFiltersLength(facets)

  const { items = [] } = findCategoryFacet(facets) ?? {}
  const { query, active_deals: activeDeals, new_deals: newDeals } = queryParams
  const { lineageDepth = 0, name: categoryName } = currentCategory || {}

  const lineageToDisplay = lineageDepth ? items.slice(0, lineageDepth + 1) : []
  const isLoadingTitle = isFetching && !categoryName

  const getBrandNames = () => {
    const [firstBrand, secondBrand] = activeBrandFilters
    const brandsLength = activeBrandFilters.length
    if (brandsLength === 1) return firstBrand
    if (brandsLength === 2) return `${firstBrand} & ${secondBrand}`
    if (brandsLength >= 3) return `${brandsLength} brands`
  }

  const getRoute = (categoryId: number) => {
    const params = { ...queryParams, category_id: categoryId }
    const stringifiedParams = stringify(params)
    return `${pathname}?${stringifiedParams}`
  }

  const getTitle = (): string => {
    let retStr = ''
    if (newDeals) {
      retStr = 'New Deals This Week '
      retStr += query ? `on “${query}”` : categoryName ? `on ${categoryName}` : ''
    } else {
      retStr = activeDeals ? 'Deals on ' : ''
      retStr += query ? `“${query}”` : categoryName || `${activeDeals ? 'a' : 'A'}ll items`
    }
    return retStr
  }

  const handleOnClickMobileFilter = () =>
    openDialog(DynamicSearchFilterModal, { filterToAutoExpand: '' })

  return (
    <Wrapper>
      {!query && lineageToDisplay.length > 0 && (
        <nav aria-label="breadcrumbs">
          <ScrollableBreadcrumbs>
            {lineageToDisplay.map(({ id, name = '' }, index) => {
              const isLastItem = index === lineageToDisplay.length - 1
              return isLastItem ? (
                <li key={id}>
                  <Breadcrumb>{name}</Breadcrumb>
                </li>
              ) : (
                <li key={id}>
                  <NextLink href={getRoute(id)}>
                    <Breadcrumb>{name}</Breadcrumb>
                  </NextLink>
                  <ChevronRightIcon size="sm" inherit />
                </li>
              )
            })}
          </ScrollableBreadcrumbs>
        </nav>
      )}
      <TitleRow>
        <Heading size="sm" as="h2">
          <SkeletonLoader isLoading={isLoadingTitle} width={200} height={32}>
            <span>{getTitle()}</span>
            {activeBrandFilters.length > 0 && <span>: {getBrandNames()}</span>}
            <Breadcrumb variant="secondary">
              {`${total_hits}\u00A0${total_hits > 1 ? 'items' : 'item'}`}
            </Breadcrumb>
          </SkeletonLoader>
        </Heading>
        <MobileFilterButton
          onClick={handleOnClickMobileFilter}
          activeFiltersLength={activeFiltersLength}
        />
      </TitleRow>
    </Wrapper>
  )
}

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  padding-right: 0.75rem;
`

const Wrapper = styled.div`
  border: none;

  @media ${screenSizes.tablet} {
    border-bottom: 0.0625rem solid ${({ theme }) => theme.gray300};
  }
`

const Breadcrumb = styled(Body)`
  display: inline;
`

const ScrollableBreadcrumbs = styled.ol`
  white-space: nowrap;
  overflow: auto hidden;
  height: 1.5rem;
  padding-left: 0;
  margin: 0;

  & span {
    vertical-align: top;
  }

  & svg {
    vertical-align: top;
    margin: 0 0.25rem;

    @media ${screenSizes.tablet} {
      margin: 0 0.5rem;
    }
  }

  ${hideScrollBar}

  svg,
  a,
  p {
    color: ${({ theme }) => theme.gray600};
  }

  li {
    list-style: none;
    display: inline-block;
  }
`

const Heading = styled(Display)`
  & > p:last-child {
    margin-left: 0.5rem;
  }

  @media ${screenSizes.tablet} {
    margin-bottom: 0.5rem;
  }
`
